/**
 * Login view (E10-F01-T05) — the anonymous gate shown when there is no valid
 * session (cold start without a token, a failed restore, logout, or any 401 that
 * fired `handleUnauthorized`). Parity with the legacy `#login-screen`: a centred
 * navy card with the gold 'EER GLOBAL' wordmark over an email + password form.
 *
 * The view is dumb: it only collects credentials and renders the in-flight /
 * error state from `useAuth`. The actual transport + session persistence + data
 * warm-up live in `useAuth.login` (POST /auth/login → setSession → load lists).
 *
 * Copy/branding is a 1:1 port of the legacy `#login-view` (index.html): the
 * globe icon (`fa-globe`), the 'EER GLOBAL' wordmark, the 'Project Launch
 * Automation' tagline, the `name@eer.com` / `******` placeholders and the
 * 'Log in' button caption.
 */
import { useState, type FormEvent } from 'react';
import type { LoginCredentials } from './api.js';

export interface LoginViewProps {
  /** Submit handler (resolves true on success); from `useAuth.login`. */
  readonly onLogin: (credentials: LoginCredentials) => Promise<boolean>;
  /** True while a login request is in flight (disables the submit button). */
  readonly loggingIn: boolean;
  /** Last login error message, or null. */
  readonly error: string | null;
  /**
   * Opens the password-reset flow (E01-F03, NEW in v2). When provided, a
   * "Forgot password?" link is rendered under the submit button — the only UI
   * entry point to `POST /auth/request-reset` for a user who cannot log in.
   */
  readonly onForgotPassword?: () => void;
  /** Informational line above the form (e.g. "password updated"), or null. */
  readonly notice?: string | null;
}

export function LoginView({
  onLogin,
  loggingIn,
  error,
  onForgotPassword,
  notice = null,
}: LoginViewProps): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (loggingIn) return;
    void onLogin({ email, password });
  };

  return (
    <div
      id="login-screen"
      data-testid="login-screen"
      className="flex min-h-screen items-center justify-center bg-slate-900 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-2xl"
        aria-label="Log in"
      >
        {/* Brand block — globe icon + wordmark + tagline (legacy #login-view) */}
        <div className="mb-6">
          <i
            className="fa-solid fa-globe mb-2 text-6xl text-slate-800"
            data-testid="login-globe"
            aria-hidden
          />
          <h1 className="text-3xl font-bold tracking-wider text-slate-900">EER GLOBAL</h1>
          <p className="mt-1 text-sm uppercase tracking-widest text-slate-500">
            Project Launch Automation
          </p>
        </div>

        {notice ? (
          <p data-testid="login-notice" className="mb-4 text-sm text-text-soft" role="status">
            {notice}
          </p>
        ) : null}

        <label
          className="mb-1 block text-left text-sm font-medium text-text-strong"
          htmlFor="login-email"
        >
          Email
        </label>
        <input
          id="login-email"
          data-testid="login-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@eer.com"
          className="mb-4 w-full rounded border border-border px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
        />

        <label
          className="mb-1 block text-left text-sm font-medium text-text-strong"
          htmlFor="login-password"
        >
          Password
        </label>
        <input
          id="login-password"
          data-testid="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="******"
          className="mb-4 w-full rounded border border-border px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
        />

        {error ? (
          <p data-testid="login-error" className="mb-4 text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={loggingIn}
          className="w-full rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {loggingIn ? 'Logging in…' : 'Log in'}
        </button>

        {onForgotPassword ? (
          <button
            type="button"
            data-testid="login-forgot-password"
            onClick={onForgotPassword}
            className="mt-4 text-sm text-text-soft underline hover:text-text-strong"
          >
            Forgot password?
          </button>
        ) : null}
      </form>
    </div>
  );
}
